import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { PageHeader } from './PageHeader';
import { HelpModal } from './HelpModal';
import { Footer } from './Footer';
import { ENErrorDisplay } from './ENErrorDisplay';
import { validateForm, getErrorMessages } from '../services/validation';
import { getValidationRuleset } from '../config/validationRules';
import { submitENFormAjax, type ENError } from '../services/enAjaxSubmission';
import { useCountries } from '../hooks/useCountries';
import type { Consultation } from '../types/consultation';

interface SignupScreenProps {
  onComplete: (data: {
    firstName: string;
    lastName: string;
    email: string;
    country: string;
    marketingConsent: boolean;
  }) => void;
  config?: Consultation | null;
}

export function SignupScreen({ onComplete, config }: SignupScreenProps) {
  const { t, i18n } = useTranslation('signup');
  const { t: tApp } = useTranslation('application');
  const { countries, isLoading: isLoadingCountries } = useCountries();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    country: ''
  });
  const [marketingConsent, setMarketingConsent] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [enErrors, setEnErrors] = useState<ENError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [generalError, setGeneralError] = useState<string>('');

  // Check if marketing consent should be shown based on consultation config
  const showMarketingConsent = config?.showMarketingConsent ?? true;

  // Check if form is valid
  const isFormValid = () => {
    const hasRequiredFields = 
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.country !== '';
    
    const hasConsent = !showMarketingConsent || marketingConsent !== '';
    
    return hasRequiredFields && hasConsent;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({});
    setEnErrors([]);
    setGeneralError('');
    
    // Check if form is incomplete before validation
    if (!isFormValid()) {
      setGeneralError(t('validation.allFieldsRequired'));
      setIsSubmitting(false);
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Get the validation ruleset for the current language
      const ruleset = getValidationRuleset(i18n.language);

      // Prepare data for validation
      const dataToValidate = {
        ...formData,
        marketingConsent: showMarketingConsent ? marketingConsent : 'no',
      };

      // Validate the form using language-specific rules
      const validationResult = validateForm(dataToValidate, ruleset, formData.country);

      if (!validationResult.valid) {
        // Convert validation errors to the format expected by the component
        const errorMessages = getErrorMessages(validationResult);
        setErrors(errorMessages);
        setIsSubmitting(false);
        return;
      }

      // Prepare user data
      const userData = {
        ...formData,
        marketingConsent: showMarketingConsent ? marketingConsent === 'yes' : false
      };

      // Save to localStorage before submission (for other functionality)
      localStorage.setItem('consultationUser', JSON.stringify(userData));
      
      // Save email to sessionStorage for comment form (GDPR-compliant - clears on tab close)
      sessionStorage.setItem('consultationEmail', formData.email);

      // Submit via AJAX to EN data capture page
      const response = await submitENFormAjax(userData, i18n.language);

      if (!response.success) {
        // Handle errors
        if (response.errors && response.errors.length > 0) {
          console.error('[SignupScreen] EN validation errors:', response.errors);
          setEnErrors(response.errors);
        } else {
          setEnErrors([{
            message: 'An unexpected error occurred. Please try again.',
          }]);
        }
        setIsSubmitting(false);
        return;
      }

      // Success - navigate to page 2
      // The response contains page 2 HTML, but we'll navigate to it
      // Extract the URL from the response or use the endpoint
      console.log('[SignupScreen] Form submitted successfully, navigating to page 2');
      
      // Call onComplete to proceed to next screen
      onComplete(userData);
    } catch (error) {
      console.error('[SignupScreen] Form submission error:', error);
      setEnErrors([{
        message: 'An unexpected error occurred. Please try again.',
      }]);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <HelpModal 
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />
      <PageHeader onHelpClick={() => setShowHelpModal(true)} />
      <main role="main" className="flex-1 flex items-center justify-center px-8 py-4">
        <div className="max-w-3xl w-full">
          <div className="text-center mb-8">
          <h1 className="title mb-4">{t('title')}</h1>
          <p className="max-w-xl mx-auto">
            {t('subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-border p-8 space-y-6">
          {/* Display general validation error */}
          {generalError && (
            <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded mb-4">
              <p className="text-sm font-medium">{generalError}</p>
            </div>
          )}
          
          {/* Display EN validation errors */}
          {enErrors.length > 0 && (
            <ENErrorDisplay
              errors={enErrors}
              onDismiss={() => setEnErrors([])}
            />
          )}

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="firstName" className="font-bold">
                {t('fields.firstName')} <span className="text-destructive font-normal">{t('fields.required')}</span>
              </Label>
              <Input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => {
                  const value = e.target.value;
                  const truncated = value.slice(0, 40);
                  const wasTruncated = value.length > 40;
                  
                  setFormData({ ...formData, firstName: truncated });
                  
                  if (wasTruncated) {
                    setErrors({ ...errors, firstName: t('validation.firstNameMaxLength') });
                  } else if (errors.firstName === t('validation.firstNameMaxLength')) {
                    // Clear truncation error if user is typing valid length
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { firstName, ...rest } = errors;
                    setErrors(rest);
                  }
                }}
                maxLength={40}
                className="mt-2 border-border bg-input-background"
              />
              {errors.firstName && (
                <p className="text-destructive text-sm mt-1">{errors.firstName}</p>
              )}
            </div>

            <div>
              <Label htmlFor="lastName" className="font-bold">
                {t('fields.lastName')} <span className="text-destructive font-normal">{t('fields.required')}</span>
              </Label>
              <Input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => {
                  const value = e.target.value;
                  const truncated = value.slice(0, 40);
                  const wasTruncated = value.length > 40;
                  
                  setFormData({ ...formData, lastName: truncated });
                  
                  if (wasTruncated) {
                    setErrors({ ...errors, lastName: t('validation.lastNameMaxLength') });
                  } else if (errors.lastName === t('validation.lastNameMaxLength')) {
                    // Clear truncation error if user is typing valid length
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { lastName, ...rest } = errors;
                    setErrors(rest);
                  }
                }}
                maxLength={40}
                className="mt-2 border-border bg-input-background"
              />
              {errors.lastName && (
                <p className="text-destructive text-sm mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="email" className="font-bold">
              {t('fields.email')} <span className="text-destructive font-normal">{t('fields.required')}</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              maxLength={254}
              className="mt-2 border-border bg-input-background"
            />
            {errors.email && (
              <p className="text-destructive text-sm mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <Label htmlFor="country" className="font-bold">
              {t('fields.country')} <span className="text-destructive font-normal">{t('fields.required')}</span>
            </Label>
            <select
              id="country"
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              disabled={isLoadingCountries}
              className="mt-2 w-full border border-border bg-input-background px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">{isLoadingCountries ? t('fields.loading', 'Loading...') : t('fields.countryPlaceholder')}</option>
              {countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
            {errors.country && (
              <p className="text-destructive text-sm mt-1">{errors.country}</p>
            )}
          </div>

          {showMarketingConsent && (
            <div className="space-y-4">
              <div>
                <h4 className="mb-2 font-bold">
                  {t('stayConnected.title')} <span className="text-destructive">{t('fields.required')}</span>
                </h4>
                <p className="text-[16px] mb-4">
                  {t('stayConnected.description')}{' '}
                  <a 
                    href={tApp('footer.privacyPolicyUrl')} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#4D2200] underline"
                  >
                    {t('stayConnected.privacyLink')}
                  </a>.
                </p>
              </div>

              <RadioGroup value={marketingConsent} onValueChange={setMarketingConsent}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="consent-yes" />
                  <Label htmlFor="consent-yes" className="cursor-pointer">
                    {t('stayConnected.yesLabel')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="consent-no" />
                  <Label htmlFor="consent-no" className="cursor-pointer">
                    {t('stayConnected.noLabel')}
                  </Label>
                </div>
              </RadioGroup>

              {errors.consent && (
                <p className="text-destructive text-sm">{errors.consent}</p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !isFormValid()}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-[Sofia_Sans_Condensed] text-[24px] font-bold text-center px-[32px] py-[12px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? t('buttons.submitting', 'Submitting...') : t('buttons.submit')}
          </button>
        </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
